import { createContext, useContext, useMemo, type ReactNode } from "react";
import { analyze, type Analysis, type YearFile } from "@ftr/tax-core";
import type { SaveState } from "../lib/yearStore.ts";

export type StepId = "you" | "futures" | "prop" | "review" | "report";

interface Ctx {
  yf: YearFile;
  analysis: Analysis;
  update: (fn: (yf: YearFile) => YearFile) => void;
  replace: (yf: YearFile) => void;
  saveState: SaveState;
  go: (step: StepId) => void;
}

const OrganizerContext = createContext<Ctx | null>(null);

export function OrganizerProvider(props: Omit<Ctx, "analysis"> & { children: ReactNode }) {
  const analysis = useMemo(() => analyze(props.yf), [props.yf]);
  const value = useMemo(() => ({ ...props, analysis }), [props, analysis]);
  return <OrganizerContext.Provider value={value}>{props.children}</OrganizerContext.Provider>;
}

export function useOrganizer(): Ctx {
  const c = useContext(OrganizerContext);
  if (!c) throw new Error("useOrganizer outside OrganizerProvider");
  return c;
}

/** Wraps an edit so a thrown validation message reaches the user instead of crashing the page. */
export function safely(fn: () => void, onError: (msg: string) => void) {
  try {
    fn();
  } catch (e) {
    onError(e instanceof Error ? e.message : String(e));
  }
}
